// The right-side inspector for the time-off heatmap. Whenever a (doctor ×
// month) cell is active, it shows that doctor and the entries that intersect
// that month. Mirrors `slots-heatmap/detail-rail.jsx` but without the
// bulk-color popover (time off has no `colorHex`) and with type-accented entry
// cards instead of slot-shaped articles.

import { Pencil, PartyPopper, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { HueAvatar } from "../slots-heatmap/heatmap-bits";
import { MONTH_LONG, entryAccent, formatEntryRange } from "./time-off-utils";

const GLOBAL_KEY = "__global__";

// One entry card. Mirrors the EntryCard in `day-view-time-off-panel.jsx` but
// compressed for the 300px rail. The rose/amber/slate left-rail accent comes
// from `entryAccent(row).cardClass`.
function EntryCard({ row, onEdit, onDelete }) {
  const accent = entryAccent(row);
  const Icon = accent.icon;
  const hasActions = Boolean(onEdit || onDelete);
  const editable = Boolean(onEdit);
  return (
    <article
      onDoubleClick={editable ? () => onEdit(row) : undefined}
      title={editable ? "Double-click to edit" : undefined}
      className={cn(
        "group relative overflow-hidden rounded-md border border-border shadow-xs transition-colors hover:bg-muted/40",
        accent.cardClass,
        editable && "cursor-pointer",
      )}
    >
      <div className="block px-2 py-1.5 pr-8">
        <div className="flex items-baseline gap-1.5 text-xs leading-tight text-foreground">
          <Icon className={cn("size-3.5 shrink-0", accent.iconClass)} aria-hidden />
          <span className="min-w-0 truncate font-medium">{row.title}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[0.6875rem] leading-tight text-muted-foreground">
          <span className="tabular-nums">{formatEntryRange(row)}</span>
          {row.scope === "global" ? (
            <Badge variant="secondary" className="px-1 py-0 text-[10px]">
              Global
            </Badge>
          ) : null}
          {row.doctor ? <span className="truncate">· {row.doctor}</span> : null}
          {row.source === "imported" ? (
            <Badge variant="outline" className="px-1 py-0 text-[10px]">
              Imported
            </Badge>
          ) : null}
          {row.customized ? (
            <Badge
              variant="outline"
              className="px-1 py-0 text-[10px] text-amber-600 dark:text-amber-300"
            >
              Edited
            </Badge>
          ) : null}
        </div>
      </div>

      {hasActions ? (
        <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5">
          {onEdit ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onEdit(row);
              }}
              onDoubleClick={(event) => event.stopPropagation()}
              title="Edit time off"
              aria-label="Edit time off"
              className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground/60 opacity-0 transition-[color,opacity] hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover:opacity-100"
            >
              <Pencil className="size-3.5" aria-hidden />
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onDelete(row);
              }}
              onDoubleClick={(event) => event.stopPropagation()}
              title="Delete time off"
              aria-label="Delete time off"
              className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground/60 opacity-0 transition-[color,opacity] hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover:opacity-100"
            >
              <Trash2 className="size-3.5" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export function DetailRail({
  doctor,
  month,
  year,
  rows = [],
  onAddTimeOff,
  onEditEntry,
  onDeleteEntry,
}) {
  if (!doctor) return null;
  const monthName = MONTH_LONG[month - 1] || "";
  const isGlobal = doctor.id === GLOBAL_KEY;

  return (
    <aside className="flex w-[300px] shrink-0 flex-col border-l border-border bg-muted/30">
      <div className="border-b border-border p-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground/60">
          Inspecting
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          {isGlobal ? (
            <span className="inline-grid size-[26px] shrink-0 place-items-center rounded-full bg-rose-500/15 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300">
              <PartyPopper className="size-3.5" aria-hidden />
            </span>
          ) : (
            <HueAvatar name={doctor.name} id={doctor.id} size={26} />
          )}
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-foreground">
              {doctor.name}
            </div>
            <div className="text-[11px] text-muted-foreground">
              in {monthName} {year}
            </div>
          </div>
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="flex flex-col gap-1.5 p-3">
          {rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-4 text-center text-[11px] text-muted-foreground/60">
              No time off this month
            </div>
          ) : null}
          {rows.map((row) => (
            <EntryCard
              key={row.id}
              row={row}
              onEdit={onEditEntry}
              onDelete={onDeleteEntry}
            />
          ))}
          {onAddTimeOff ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-1 w-full justify-center [&_svg]:size-3.5"
              onClick={onAddTimeOff}
            >
              <Plus />
              {isGlobal ? "Add global time off to this month" : "Add time off to this month"}
            </Button>
          ) : null}
        </div>
      </ScrollArea>
    </aside>
  );
}
